import { fetchJSON as fetchAPI } from './client';

export type HealthScoreComponent = {
  key: string; name: string; current_value?: string; target?: string; gap?: string;
  priority?: string; business_impact?: string; evidence?: string[];
};

export type ListingHealthScore = {
  asin: string; overall_score: number; components: HealthScoreComponent[];
  dna_origin: boolean; generated_at: string;
};

export type GrowthRecommendation = {
  id: string; title: string; category: string; business_problem: string;
  evidence?: string[]; root_cause?: string; expected_ctr?: string; expected_cvr?: string;
  expected_organic_lift?: string; expected_revenue?: string; expected_profit?: string;
  confidence: number; priority: string; effort: string; risk: string;
  rollback_strategy?: string; observation_window?: string; status: string;
  action_label: string; dna_evidence: boolean;
};

export type SimulationResult = {
  asin: string; recommendation_id: string; estimated_ctr: string; estimated_cvr: string;
  estimated_revenue: string; estimated_profit: string; organic_rank_impact?: string;
  confidence: number; risk: string; historical_similarity?: string; key_assumptions?: string[];
  generated_at: string;
};

export type ContentVersion = {
  id: string; asin: string; version: number; status: string; author?: string;
  approved: boolean; created_at: string; summary?: string;
};

export type ExperimentLink = {
  id: string; asin: string; name: string; status: string;
  baseline_kpi?: string; observation_window?: string; created_at: string;
};

export type CompetitorGap = {
  dimension: string; our_score: number; competitor_avg: number; gap: number; priority: string;
};

export type ListingTimelineEvent = {
  id: string; asin: string; type: string; title: string; actor?: string; timestamp: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const listingGrowthIntelligenceApi = {
  health: (asin: string) => fetchCmd<ListingHealthScore>(`/listing-growth/${encodeURIComponent(asin)}/health`),
  recommendations: (asin: string) =>
    fetchCmd<{ recommendations: GrowthRecommendation[] }>(`/listing-growth/${encodeURIComponent(asin)}/recommendations`).then((r) => r.recommendations),
  simulate: (asin: string, recommendationId: string) =>
    fetchCmd<SimulationResult>(`/listing-growth/${encodeURIComponent(asin)}/simulate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recommendation_id: recommendationId }),
    }),
  versions: (asin: string) =>
    fetchCmd<{ versions: ContentVersion[] }>(`/listing-growth/${encodeURIComponent(asin)}/versions`).then((r) => r.versions),
  experiments: (asin: string) =>
    fetchCmd<{ experiments: ExperimentLink[] }>(`/listing-growth/${encodeURIComponent(asin)}/experiments`).then((r) => r.experiments),
  history: (asin: string) =>
    fetchCmd<{ history: ListingTimelineEvent[] }>(`/listing-growth/${encodeURIComponent(asin)}/history`).then((r) => r.history),
  competitor: (asin: string) =>
    fetchCmd<{ gaps: CompetitorGap[] }>(`/listing-growth/${encodeURIComponent(asin)}/competitor`).then((r) => r.gaps),
};
